import axios from 'utils/axios';

// ==============================|| TOKEN UTILITIES ||============================== //

/**
 * Check if a token is expired
 * @param {string} expiresAt - Token expiration date string
 * @returns {boolean} - True if token is expired or invalid
 */
export const isTokenExpired = (expiresAt) => {
  if (!expiresAt) return true;

  try {
    const expirationDate = new Date(expiresAt);
    const currentDate = new Date();
    return currentDate >= expirationDate;
  } catch (error) {
    console.error('Error parsing token expiration date:', error);
    return true;
  }
};

/**
 * Get time until token expires in milliseconds
 * @param {string} expiresAt - Token expiration date string
 * @returns {number} - Milliseconds until expiration, negative if expired
 */
export const getTimeUntilExpiration = (expiresAt) => {
  if (!expiresAt) return -1;

  try {
    const expirationDate = new Date(expiresAt);
    const currentDate = new Date();
    return expirationDate.getTime() - currentDate.getTime();
  } catch (error) {
    console.error('Error calculating time until expiration:', error);
    return -1;
  }
};

/**
 * Format expiration time for display
 * @param {string} expiresAt - Token expiration date string
 * @returns {string} - Formatted time string
 */
export const formatExpirationTime = (expiresAt) => {
  if (!expiresAt) return 'No expiration set';

  try {
    const expirationDate = new Date(expiresAt);
    const currentDate = new Date();
    const diffMs = expirationDate.getTime() - currentDate.getTime();

    if (diffMs <= 0) {
      return 'Expired';
    }

    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffDays > 0) {
      return `${diffDays}d ${diffHours}h ${diffMinutes}m`;
    } else if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes}m`;
    } else {
      return `${diffMinutes}m`;
    }
  } catch (error) {
    console.error('Error formatting expiration time:', error);
    return 'Invalid date';
  }
};

/**
 * Validate user data structure
 * @param {object} user - User object
 * @returns {boolean} - True if user data is valid
 */
export const isValidUserData = (user) => {
  return user && typeof user === 'object' && user.id && user.first_name && user.last_name && user.email;
};

/**
 * Clear all authentication data
 */
export const clearAuthData = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  delete axios.defaults.headers.common.Authorization;
};
